import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from '../founderCall';
import type { FeatureContent } from './types';

export const jobDistribution: FeatureContent = {
  slug: 'job-distribution',
  path: '/features/job-distribution',
  scorecardConnector:
    'This fills the pipeline the scorecard is built from: one post, 25,000+ boards, every applicant tracked.',
  audience: 'company',
  icon: 'Globe',
  name: 'Job Distribution',
  stage: 1,
  navLabel: 'Job Distribution',
  navDescription: 'Post once. Reach 25,000+ boards. Track every applicant.',
  primaryKeyword: 'post to multiple job boards',
  seo: {
    title: 'Post to 25,000+ Job Boards in One Click | Talenture.ai',
    description:
      'Post to multiple job boards in one click and reach 25,000+ boards worldwide. Every change syncs automatically and every applicant tracks back into one pipeline.',
    keywords: [
      'post to multiple job boards',
      'job distribution software',
      'job posting distribution',
      'multi job board posting',
      'job advertising distribution',
      'one-click job posting',
    ],
    ogTitle: 'Post to 25,000+ Job Boards in One Click | Talenture.ai',
    ogDescription:
      'Post once and reach 25,000+ job boards worldwide. Every change syncs automatically and every applicant is tracked back into one pipeline.',
  },
  hero: {
    eyebrow: 'Stage 1 of the hiring mission · Define the role',
    whereThisFits:
      'Once the role is defined, Talenture pushes it out wide and pulls every applicant into one pipeline, ready to rank.',
    h1: 'Post once. Reach 25,000+ boards. Track every applicant',
    painOpener: 'Reposting the same role across a dozen sites, then guessing which one worked.',
    subhead:
      'No more reposting the same role across dozens of sites and losing track of who came from where. Talenture distributes your job to 25,000+ boards and pulls every applicant back into one pipeline.',
    worksWithAts: true,
    primaryCta: { label: 'See a sample scorecard', href: '/sample-scorecard' },
    secondaryCta: { label: FOUNDER_CALL_LABEL, href: FOUNDER_CALL_URL },
  },
  definition: {
    heading: 'What is job distribution?',
    body: 'Job distribution is publishing one role to many job boards at once and managing them from a single place. Talenture posts to 25,000+ boards in one click, syncs every later edit automatically, and routes all applicants back into your Talenture pipeline with their source attached, so you reach more candidates without the manual reposting.',
  },
  sections: [
    {
      icon: 'Share2',
      title: 'One click, thousands of boards',
      body: 'Publish a role once and Talenture pushes a clean, well-formatted listing out to a vast network, including the boards candidates actually search.',
      bullets: [
        'Reach 25,000+ job boards worldwide',
        'Indeed, LinkedIn, Glassdoor, and thousands more',
        'Clean, structured listings generated for you',
      ],
    },
    {
      icon: 'RefreshCw',
      title: 'Edit once, sync everywhere',
      body: 'Change the role and Talenture pushes the update only where it changed, so every board stays current without you touching each one.',
      bullets: [
        'Automatic sync of companies, recruiters, and jobs',
        'Updates pushed only on real change',
        'No manual reposting',
      ],
    },
    {
      icon: 'GitBranch',
      title: 'Every applicant, tracked back',
      body: 'Applicants from external boards flow into your Talenture pipeline with their source attached, so you know which channels work and no candidate is lost between systems.',
      bullets: [
        'Source attribution on every external apply',
        'Two-way status tracking with board stages',
        'One pipeline for every channel',
      ],
    },
  ],
  visual: {
    component: 'JobDistributionScreen',
    url: 'app.talenture.ai/roles/platform-engineer/distribution',
    caption: 'One role, every board, and the applicants arriving back in a single pipeline.',
  },
  supportingVisuals: [
    {
      component: 'ApplicationsBoardScreen',
      url: 'app.talenture.ai/roles/platform-engineer/applications',
      heading: 'Where the applicants land',
      caption:
        'Whatever board they came from, they arrive in one pipeline, already read and ranked against the role.',
    },
  ],
  howTo: {
    heading: 'How job distribution works',
    intro: 'Three steps from one role to a tracked applicant pipeline.',
    steps: [
      {
        title: 'Publish the role once',
        body: 'Talenture turns the role into a clean, structured listing and distributes it to 25,000+ job boards, including Indeed, LinkedIn, and Glassdoor.',
      },
      {
        title: 'Sync every real change',
        body: 'When you edit the role, Talenture pushes updates only where something changed, so listings stay current without manual reposting.',
      },
      {
        title: 'Track every applicant by source',
        body: 'Applicants flow back into one Talenture pipeline with source attribution and two-way status tracking between your stages and the external boards.',
      },
    ],
  },
  proofPoints: [
    { value: '25,000+', label: 'job boards reached' },
    { value: 'One click', label: 'to distribute a role' },
    { value: 'One pipeline', label: 'for every applicant source' },
  ],
  faq: [
    {
      question: 'How many job boards can I post to?',
      answer:
        'More than 25,000 boards worldwide, including Indeed, LinkedIn, Glassdoor, and thousands of niche and regional sites. You publish the role once and Talenture handles distribution across the network.',
    },
    {
      question: 'What happens when I update a job after posting?',
      answer:
        'Talenture syncs the change automatically and pushes the update only where something actually changed. You do not log in to each board or repost manually, and every listing stays current.',
    },
    {
      question: 'Can I see where my applicants came from?',
      answer:
        'Yes. External applies carry their source back to the application record, so each candidate arrives in your pipeline with attribution. You can see which boards deliver the best candidates and act on it.',
    },
    {
      question: 'Do applicants from other boards stay in one place?',
      answer:
        'They do. Every applicant, wherever they applied, flows into your Talenture pipeline, and status is tracked two ways between your internal stages and the board stages, so nothing falls through the cracks.',
    },
  ],
  relatedSlugs: ['ai-job-description-generator', 'ai-candidate-matching', 'interview-scheduling'],
};
